import { Box, Typography, useTheme, Grid2, Stack } from "@mui/material";
import { FormUnit } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { getScorePrecision } from "src/context/Scores/provider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { SingleImpactByAction } from "../../../../context/utils/impactData/formatSimpleComparisonTable";
import { useMyActionPlanContext } from "../../../../context/useMyActionPlan";
import { TableBlockHeader } from "../Common/TableBlockHeader";
import { useEffect, useState } from "react";
import { SortableRows } from "src/components/Dragging/SortableRows/SortableRows";
import { ImpactComparison } from "../Common/ImpactComparison";
import { currentActionMarkerSx, EngagedActionTitle } from "../Common/EngagedActionTitle";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";

type SimpleComparisonTableProps = {
    impactByAction: SingleImpactByAction;
};

export const SimpleComparisonTable = (props: SimpleComparisonTableProps) => {
    const { impactByAction } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const { scoresWithVariations, formatScore, getColor } = useEntityScores();
    const { scoresEnabled } = useActionsContext();
    const { actionPlanAction } = useMyActionPlanContext();
    const { hasMultipleActions, rows, property, formattedSummedActionsImpact } = impactByAction;

    const total = rows.length > 1 && formattedSummedActionsImpact !== null ? formattedSummedActionsImpact : null;

    const rowsWithId = rows.map((row) => {
        return {
            ...row,
            id: row.actionId,
        };
    });

    const [items, setItems] = useState(rowsWithId);

    useEffect(() => {
        const rowsWithId = rows.map((row) => {
            return {
                ...row,
                id: row.actionId,
            };
        });
        setItems(rowsWithId);
    }, [impactByAction]);

    const hasAnyScoreValue = items.some((item) =>
        scoresEnabled.some(
            (score) =>
                item.impactScores?.[score.id] !== undefined || item.impactScores?.[score.id.toString()] !== undefined,
        ),
    );
    if (!hasAnyScoreValue) {
        return null;
    }

    const scoreColumnSize = Math.max(2, Math.floor(12 / (scoresEnabled.length + 2)));
    const actionTitleSize = 12 - scoreColumnSize * scoresEnabled.length;

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: `1px solid ${theme.custom.grey[400]}`,
            }}
        >
            <TableBlockHeader
                parentName={property?.parent?.name ?? property?.parent?.title}
                propertyName={property.name || property.title}
                hasMultipleActions={hasMultipleActions}
                propertiesWithMultipleActions={[property.name || property.title]}
                // Here, it will not be used anyway
                position={0}
            />

            <Grid2
                size={12}
                container
                columns={12}
                bgcolor={theme.custom.grey[200]}
                minHeight={"64px"}
                paddingInline={theme.spacing(2)}
                alignItems={"center"}
                sx={{
                    borderTop: `1px solid ${theme.custom.grey[600]}`,
                }}
            >
                <Grid2 size={actionTitleSize}>
                    <Typography variant="body-bold">
                        {t("action_plan.action.engaged_action", "Engaged action")}
                    </Typography>
                </Grid2>
                {scoresEnabled.map((score) => (
                    <Grid2 key={score.id} size={scoreColumnSize} textAlign={"end"}>
                        <Typography variant="body-bold">{score.title}</Typography>
                    </Grid2>
                ))}
            </Grid2>

            <SortableRows
                items={items}
                onDragEnd={(newItems) => {
                    setItems(newItems);
                }}
                renderItem={(item, _draggableItem, index) => {
                    // DraggableItem is the Item used to drag.
                    // If you don't provide it, the row will not be draggable.
                    const actionIsCurrentPage = item.actionId === actionPlanAction.actionId;

                    const shouldShowBottomBorder = hasMultipleActions && index === items.length - 1;
                    return (
                        <Grid2
                            size={12}
                            container
                            columns={12}
                            bgcolor={"white"}
                            minHeight={"64px"}
                            paddingInline={theme.spacing(2)}
                            alignItems={"center"}
                            sx={[
                                {
                                    borderTop: `1px solid ${theme.custom.grey[600]}`,
                                    borderBottom:
                                        shouldShowBottomBorder ? `1px solid ${theme.custom.grey[600]}` : undefined,

                                    borderBottomRightRadius: !shouldShowBottomBorder ? "12px" : undefined,
                                    borderBottomLeftRadius: !shouldShowBottomBorder ? "12px" : undefined,
                                },
                                hasMultipleActions && actionIsCurrentPage && currentActionMarkerSx(theme),
                            ]}
                        >
                            <Grid2 size={actionTitleSize}>
                                <EngagedActionTitle
                                    title={item.title}
                                    isCurrentAction={actionIsCurrentPage}
                                    showCurrentActionTag={hasMultipleActions}
                                    otherActionVariant={"body-medium"}
                                />
                            </Grid2>
                            {scoresEnabled.map((score) => {
                                const scoreValue =
                                    item.impactScores?.[score.id] ?? item.impactScores?.[score.id.toString()];
                                const unit =
                                    scoresWithVariations.find((row) => row.scoreId === score.id)?.unit ??
                                    score.unit?.abbreviation ??
                                    "";
                                return (
                                    <Grid2
                                        key={score.id}
                                        size={scoreColumnSize}
                                        display={"inline-grid"}
                                        justifyItems={"end"}
                                    >
                                        {scoreValue === undefined ?
                                            <Typography variant="body-medium">
                                                {t("action_plan.action.non_quantifiable", "Not quantifiable")}
                                            </Typography>
                                        :   <ImpactComparison
                                                score={scoreValue}
                                                value={{
                                                    start: item.startElement,
                                                    end: item.endElement,
                                                    change: item.elementChange,
                                                    fieldType: item.fieldType,
                                                    unit: item.propertyUnit,
                                                }}
                                                scoreImprovement={score.improvement}
                                                scorePrecision={getScorePrecision(score)}
                                                scoreUnit={unit}
                                            />
                                        }
                                    </Grid2>
                                );
                            })}
                        </Grid2>
                    );
                }}
            />

            {/* Total Footer */}
            {hasMultipleActions && (
                <Grid2
                    size={12}
                    container
                    columns={12}
                    bgcolor={theme.custom.grey[200]}
                    borderRadius="12px"
                    height={"64px"}
                    paddingInline={theme.spacing(2)}
                    alignItems={"center"}
                >
                    <Grid2 size={actionTitleSize}>
                        <Typography variant={"body-bold"}>{t("total", "Total")}</Typography>
                    </Grid2>
                    {scoresEnabled.map((score) => {
                        const columnTotal = items.reduce((acc, row) => {
                            const value = row.impactScores?.[score.id];
                            return value !== undefined ? acc + value : acc;
                        }, 0);
                        const unit =
                            scoresWithVariations.find((row) => row.scoreId === score.id)?.unit ??
                            score.unit?.abbreviation ??
                            "";
                        const precision = getScorePrecision(score);
                        const formatted = formatScore(columnTotal, { precision }) ?? "—";
                        return (
                            <Grid2 key={score.id} size={scoreColumnSize} display={"inline-grid"} justifyItems={"end"}>
                                <Stack flexDirection={"row"}>
                                    <ApproximateImpactTooltip value={columnTotal} precision={precision} unit={unit}>
                                        <FormUnit
                                            value={formatted}
                                            unit={unit}
                                            color={getColor(columnTotal, { improvement: score.improvement, precision })}
                                        />
                                    </ApproximateImpactTooltip>
                                </Stack>
                            </Grid2>
                        );
                    })}
                </Grid2>
            )}
        </Box>
    );
};
